/**
 * EmailTemplatesService - Firestore CRUD for email template configuration documents
 *
 * Manages stored email template configurations in Firestore collection 'email-templates'.
 * Extends FirestoreService for standard CRUD operations.
 *
 * Boundary: Only manages template metadata/config, not actual email sending or HTML generation
 * External dependencies:
 * - Firestore: 'email-templates' collection
 * - Router: Injected but usage unclear; may be unused
 */

import { Injectable, inject } from '@angular/core';
import { limit, query, where } from '@angular/fire/firestore';
import { EmailTemplate } from '../models/email-template';
import { Router } from '@angular/router';
import { FirestoreService } from './firestore.abstract.service';

/**
 * Manages email template configuration documents in Firestore.
 * Provides CRUD operations inherited from FirestoreService.
 *
 * Dependencies:
 * - Router: Injected (TODO: Clarify router usage - may be unused)
 * - FirestoreService: Abstract base for Firestore CRUD
 *
 * Side effects:
 * - Firestore reads/writes to 'email-templates' collection
 */
@Injectable({
  providedIn: 'root',
})
export class EmailTemplatesService extends FirestoreService<EmailTemplate> {
  router = inject(Router);

  protected basePath: string = 'email-templates';

  /**
   * Looks a template up by its stable `name` field rather than by document id.
   *
   * Document ids are auto-generated and therefore differ between the dev and
   * production Firebase projects, which is what broke company invites in
   * production (dashboard#863). `name` is seeded identically in every project,
   * so one build works against all of them.
   *
   * Never throws: callers fall back to a built-in default, and a lookup failure
   * must not be indistinguishable from a send failure.
   *
   * @param name - Stable template name, e.g. 'Company Invite'
   * @returns Promise resolving to the matching template, or null when no
   *          document matches or the read fails
   */
  async getTemplateByName(name: string): Promise<EmailTemplate | null> {
    if (!name || name.trim() === '') {
      return null;
    }

    try {
      const snapshot = await this.getCollection$((collectionRef: any) =>
        query(collectionRef, where('name', '==', name), limit(1)),
      );

      const docs = snapshot?.docs ?? [];
      if (!docs.length) {
        return null;
      }

      const match = docs[0];
      return new EmailTemplate({ ...match.data(), uid: match.id });
    } catch (error) {
      this.loggingService.logError('Failed to load email template by name', {
        name,
        basePath: this.basePath,
        error: error instanceof Error ? error.message : String(error),
      });
      return null;
    }
  }
}
